import React, { useEffect } from 'react';
import { Modal, Form, Input, Select, Switch, Button } from 'antd';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import moment from 'moment';

const { Option } = Select;
const { TextArea } = Input;

const ruleTypes = [
  "SMS_OTP",
  "FACIAL_CHECK",
  "TRANSFER_TIME_CHECK",
  "ADMIN_APPROVAL"
];

const ManageRuleModal = ({ showModal, setShowModal, handleSubmitCreate, handleSubmitUpdate, editingRule }) => {
  const formik = useFormik({
    initialValues: {
      ruleType: '',
      isEnabled: true,
      amountThreshold: 0,
      startTime: null,
      endTime: null,
      description: '',
    },
    validationSchema: Yup.object({
      ruleType: Yup.string().required('Rule Type is required'),
      amountThreshold: Yup.number().min(0, 'Amount must be non-negative').required('Amount Threshold is required'),
      startTime: Yup.mixed().nullable(),
      endTime: Yup.mixed().nullable(),
      description: Yup.string().max(255, 'Description too long'),
    }),
    onSubmit: (values) => {
      const payload = {
        ruleType: values.ruleType,
        isEnabled: values.isEnabled,
        amountThreshold: values.amountThreshold,
        description: values.description,
        startTime: values.startTime ? values.startTime.format('HH:mm') : null,
        endTime: values.endTime ? values.endTime.format('HH:mm') : null,
      };
      if (editingRule) {
        handleSubmitUpdate(editingRule.id, payload);
      } else {
        handleSubmitCreate(payload);
      }
    },
  });

  useEffect(() => {
    if (editingRule) {
      formik.setValues({
        ...editingRule,
        startTime: editingRule.startTime ? moment(editingRule.startTime, 'HH:mm') : null,
        endTime: editingRule.endTime ? moment(editingRule.endTime, 'HH:mm') : null,
      });
    } else {
      formik.resetForm();
    }
  }, [editingRule, showModal]);

  return (
    <Modal
      open={showModal}
      title={editingRule ? "Edit Interception Rule" : "Create New Interception Rule"}
      onCancel={() => setShowModal(false)}
      footer={null}
      centered
    >
      <Form layout="vertical" onFinish={formik.handleSubmit}>
        <Form.Item label="Rule Type" required>
          <Select
            name="ruleType"
            value={formik.values.ruleType}
            onChange={(value) => formik.setFieldValue('ruleType', value)}
            onBlur={() => formik.setFieldTouched('ruleType', true)}
            placeholder="Select a rule type"
            disabled={!!editingRule} // Disable rule type editing for existing rules
          >
            {ruleTypes.map(type => <Option key={type} value={type}>{type.replace(/_/g, ' ')}</Option>)}
          </Select>
          {formik.touched.ruleType && formik.errors.ruleType && (
            <div style={{ color: 'red', fontSize: 12 }}>{formik.errors.ruleType}</div>
          )}
        </Form.Item>

        <Form.Item label="Amount Threshold" required>
          <Input
            type="number"
            name="amountThreshold"
            value={formik.values.amountThreshold}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            min={0}
          />
          {formik.touched.amountThreshold && formik.errors.amountThreshold && (
            <div style={{ color: 'red', fontSize: 12 }}>{formik.errors.amountThreshold}</div>
          )}
        </Form.Item>

        <Form.Item label="Start Time">
          <Input
            type="time"
            name="startTime"
            value={formik.values.startTime ? formik.values.startTime.format('HH:mm') : ''}
            onChange={(e) => {
              const timeString = e.target.value;
              formik.setFieldValue('startTime', timeString ? moment(timeString, 'HH:mm') : null);
            }}
            onBlur={() => formik.setFieldTouched('startTime', true)}
            style={{ width: '100%' }}
          />
          {formik.touched.startTime && formik.errors.startTime && (
            <div style={{ color: 'red', fontSize: 12 }}>{formik.errors.startTime}</div>
          )}
        </Form.Item>

        <Form.Item label="End Time">
          <Input
            type="time"
            name="endTime"
            value={formik.values.endTime ? formik.values.endTime.format('HH:mm') : ''}
            onChange={(e) => {
              const timeString = e.target.value;
              formik.setFieldValue('endTime', timeString ? moment(timeString, 'HH:mm') : null);
            }}
            onBlur={() => formik.setFieldTouched('endTime', true)}
            style={{ width: '100%' }}
          />
          {formik.touched.endTime && formik.errors.endTime && (
            <div style={{ color: 'red', fontSize: 12 }}>{formik.errors.endTime}</div>
          )}
        </Form.Item>

        <Form.Item label="Description">
          <TextArea
            name="description"
            value={formik.values.description}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            rows={3}
          />
          {formik.touched.description && formik.errors.description && (
            <div style={{ color: 'red', fontSize: 12 }}>{formik.errors.description}</div>
          )}
        </Form.Item>

        <Form.Item label="Is Enabled" valuePropName="checked">
          <Switch
            name="isEnabled"
            checked={formik.values.isEnabled}
            onChange={(checked) => formik.setFieldValue('isEnabled', checked)}
          />
        </Form.Item>

        <Form.Item>
          <Button type="primary" htmlType="submit" loading={formik.isSubmitting}>
            {editingRule ? "Update Rule" : "Create Rule"}
          </Button>
          <Button onClick={() => setShowModal(false)} style={{ marginLeft: 8 }}>
            Cancel
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ManageRuleModal;
